// Centre roster signals (progress-views design §7c): per linked child, for this
// week — days played, steps that moved up a stage, steps "with a little help"
// for 6+ weeks (a neutral count, never "stuck") and suggestions the family saw.
// Pure; uses only what the caller was already allowed to read.

import type { Assignment, SessionSummary, SkillStepState } from "../domain/types";
import { addDays, localDay, mondayOf } from "./weeks";
import { effectiveStageDates, stageOn, STAGES } from "./stages";
import { skillById } from "./registry";

export interface WeekSignals {
  daysPlayed: number;
  stepsMovedUp: number;
  longHelp: number;
  suggestionsSeen: number;
}

export const LONG_HELP_WEEKS = 6;

export function weekSignals(
  input: {
    sessions: readonly SessionSummary[];
    stepStates: readonly SkillStepState[];
    assignments: readonly Assignment[];
  },
  now: Date = new Date(),
): WeekSignals {
  const today = localDay(now);
  const from = addDays(today, -6); // the last 7 days, today included
  // Step dates are Monday-rounded, so "this week" for a step is its week's Monday onward.
  const fromWeek = mondayOf(from);

  const days = new Set<string>();
  for (const s of input.sessions) {
    const d = localDay(s.at);
    if (d >= from && d <= today) days.add(d);
  }

  let stepsMovedUp = 0;
  let longHelp = 0;
  const helpCutoff = addDays(today, -LONG_HELP_WEEKS * 7);
  for (const st of input.stepStates) {
    if (!skillById(st.skillId)) continue;
    const dates = effectiveStageDates(st);
    if (dates.some((d) => d !== null && d >= fromWeek && d <= today)) stepsMovedUp++;
    const help = dates[STAGES.indexOf("help")];
    if (stageOn(st, today) === "help" && help !== null && help <= helpCutoff) longHelp++;
  }

  const weekAgo = new Date(now.getTime() - 7 * 864e5).toISOString();
  const suggestionsSeen = input.assignments.filter((a) => a.seenAt && a.seenAt >= weekAgo).length;

  return { daysPlayed: days.size, stepsMovedUp, longHelp, suggestionsSeen };
}
